<template>
  <div class="page">
    <!-- Loading icon and text -->
    <div v-if="loading" class="loading-container">
      <div class="loading-content">
        <img src="../../../assets/Icons/Loading.gif" alt="Loading Icon" class="loading-animation" />
        <p class="loading-text">{{ currentLoadingMessage }}</p>
      </div>
    </div>
    <!-- Box with social media channels -->
    <div v-if="!showMetricsConstant && !loading && showBox" class="social-media-box">
      <h3>Conferma canali social per <span style="color: #2e4eed;">{{ this.sessionstore.company_name }}</span></h3>
      <p>Sito web: {{ this.sessionstore.website }}</p>
      <!-- Loop through filteredSocialChannels -->
      <div v-for="(channel, index) in filteredSocialChannels" :key="index" class="channel">
        <!-- SVG icon -->
        <component :is="channel.icon" />
        <p class="nome-canale">{{ channel.text }}</p>
        <!-- X icon to delete channel -->
        <XIcon class="elimina-bottone" @click="deleteChannel(index)" />
      </div>
      <!-- Confirmation message -->
      <p v-if="filteredSocialChannels.length > 0" class="sub-text">
        Abbiamo trovato questi canali social. Clicca su Conferma se sono tutti oppure eliminali o aggiungili usando la
        barra sotto.
      </p>
      <!-- Text input and dropdown -->
      <div class="add-channel">
        <input type="text" v-model="newChannelText" placeholder="Inserisci un nuovo canale" />
        <select v-model="selectedChannel">
          <option v-for="channel in availableChannels" :key="channel" :value="channel">{{ channel }}</option>
        </select>
        <!-- Plus icon to add channel -->
        <PlusIcon class="aggiungi-bottone" @click="addChannel" />
      </div>
      <ButtonTemplate :button-text="isConfirming ? 'Elaborazione...' : 'Conferma'"
        @click="handleSocialMediaConfirmation" class="confirm-button" :disabled="isConfirming" />
    </div>
    <div v-if="showMetricsConstant" class="metrics">
      <CheckMetrics :socialChannels="socialChannels" @click-metrics-confirm-button="showFollowingPage" />
    </div>

    <div v-if="ShowStorytellingPage" class="story-telling-page">
      <StoryTellingConfirm @advance-step="showNavigationMenu" />
    </div>
    <div v-if="ShowExperiencePage" class="experience-page">
      <ExperienceConfirm @advance-step="showNavigationMenu" />
    </div>
    <div v-if="ShowCommunityPage" class="community-page">
      <CommunityConfirm @advance-step="showNavigationMenu" />
    </div>
    <div v-if="ShowFinalPage" class="matrix-page">
      <FinalPage
        :ratings="[[sessionstore.storytellingScore, sessionstore.experienceScore, sessionstore.communityScore]]"
        :names="[sessionstore.company_name]">
      </FinalPage>
    </div>

    <div v-if="ShowNavigationMenu" class="steps">
      <StepIndicator :currentStep="CurrentIndex" @forward-event="showFollowingPage" @back-event="showPreviousPage" />
    </div>
  </div>

</template>

<script>
import ButtonTemplate from '@/components/widgets/button.vue';
import { supabase } from '@/lib/supabaseClient';
import { useSessionStore } from '@/stores/sessionStore';
import axios from 'axios';
import PlusIcon from '../../../assets/Icons/PlusIcon.vue';
import FacebookIcon from '../../../assets/Icons/SocialMedia/Facebook.vue';
import InstagramIcon from '../../../assets/Icons/SocialMedia/Instagram.vue';
import LinkedinIcon from '../../../assets/Icons/SocialMedia/Linkedin.vue';
import TiktokIcon from '../../../assets/Icons/SocialMedia/Tiktok.vue';
import TwitterIcon from '../../../assets/Icons/SocialMedia/X.vue';
import XIcon from '../../../assets/Icons/xIcon.vue';
import CheckMetrics from './CheckMetrics.vue';

import CommunityConfirm from '@/components/screens/Posizionamento/CommunityConfirm.vue';
import ExperienceConfirm from '@/components/screens/Posizionamento/ExperienceConfirm.vue';
import FinalPage from '@/components/screens/Posizionamento/FinalPage.vue';
import StoryTellingConfirm from '@/components/screens/Posizionamento/StoryTellingConfirm.vue';
import StepIndicator from '@/components/widgets/StepIndicator.vue';
import { config } from '@/config';
import { useToast } from 'vue-toastification';
import 'vue-toastification/dist/index.css';

// import { push } from 'core-js/core/array';

export default {
  setup() {
    const sessionstore = useSessionStore();
    const toast = useToast();
    return { sessionstore, toast };
  },

  name: 'SocialMediaChecker',
  components: {
    XIcon,
    PlusIcon,
    FacebookIcon,
    InstagramIcon,
    TiktokIcon,
    LinkedinIcon,
    TwitterIcon,
    ButtonTemplate,
    CheckMetrics,
    StoryTellingConfirm,
    ExperienceConfirm,
    CommunityConfirm,
    FinalPage,
    StepIndicator
  },
  data() {
    return {
      loading: true,
      showBox: true,
      isConfirming: false,
      // Using imported SVG components for icons
      socialChannels: [],
      newChannelText: '',
      selectedChannel: 'Instagram',
      availableChannels: ['Facebook', 'Instagram', 'Twitter', 'Tiktok', 'Linkedin'],
      showMetricsConstant: false,
      intervalId: null,
      ShowExperiencePage: false,
      ShowCommunityPage: false,
      ShowStorytellingPage: false,
      ShowFinalPage: false,
      CurrentIndex: 0,
      ShowNavigationMenu: false,
      storytellingScore: 0,
      experienceScore: 0,
      communityScore: 0,
      // Loading message rotation
      loadingMessages: [
        "Stiamo controllando i tuoi canali social.",
        "Stiamo controllando il tuo sito web.",
        "Stiamo calcolando le metriche."
      ],
      currentLoadingMessageIndex: 0,
      loadingIntervalId: null,
    };
  },

  computed: {
    filteredSocialChannels() {
      return this.socialChannels.filter(channel => channel.text); // Return channels with non-null text
    },
    currentLoadingMessage() {
      return this.loadingMessages[this.currentLoadingMessageIndex];
    }
  },

  mounted() {
    this.startLoadingMessageRotation();
    this.initiateProcessing();
  },

  methods: {
    getIconComponent(socialMediaName) {
      switch (socialMediaName) {
        case 'facebook': return FacebookIcon;
        case 'instagram': return InstagramIcon;
        case 'twitter': return TwitterIcon;
        case 'tiktok': return TiktokIcon;
        case 'linkedin': return LinkedinIcon;
        default: return null;
      }
    },

    async initiateProcessing() {
      this.loading = true;

      try {
        const { data, status } = await this.postCompanyToSubmit();
        if (status === 200 && data && data.success) {
          console.log('Experience metrics:', data.data.experience_metrics);
          console.log('Social media links:', data.data.social_media_links);
          this.sessionstore.setExperienceFlags(data.data.experience_metrics);
          this.sessionstore.website_contents = data.data.pages;
          console.log("Page example:", data.data.pages.slice(0, 2));
          this.pushWebsiteDataToDB();
          this.displayResults(data.data.social_media_links);
        } else {
          this.showErrorToast('Lo scraping ha incontrato un errore. Puoi inserire i dati manualmente.');
          this.displayResults({});
        }
      } catch (error) {
        this.showErrorToast('Errore imprevisto. Puoi inserire i dati manualmente. Maggiori dettagli: ' + error);
        this.displayResults({});
        console.log('Error during social media confirmation:', error);
      } finally {
        this.loading = false;
        this.stopLoadingMessageRotation();
      }
    },

    /**
     * Sends a POST request to the submit endpoint to get the company's data.
     * 
     * @returns {Promise<Object>} A promise that resolves to the API response containing:
     * @property {string} company_name - The name of the company
     * @property {string} website - The company's website URL
     * @property {string|null} instagram - Instagram profile URL if found
     * @property {string|null} facebook - Facebook profile URL if found
     * @property {boolean} flag_online_shop - Whether the company has an online shop
     * @property {boolean} flag_physical_store - Whether the company has a physical store
     * @property {boolean} flag_contacts_section - Whether the website has a contacts section
     * @property {boolean} flag_multilingual - Whether the website is multilingual
     * @property {boolean} flag_newsletter - Whether the company has a newsletter
     * @property {boolean} flag_login_option - Whether the website has login functionality
     * @property {number|null} trustpilot_score - Company's Trustpilot score if available
     * @property {Object} website_contents - JSON object containing website content analysis
     */
    async postCompanyToSubmit() {

      const path = config.API_BASE_URL + '/submit'
      console.log('Sent POST requedst to /submit...');


      try {
        const response = await axios.post(path, {
          company_name: this.sessionstore.company_name,
          website: this.sessionstore.website
        });
        return { data: response.data, status: response.status };
      } catch (error) {
        return {
          data: error.response ? error.response.data : null,
          status: error.response ? error.response.status : 500
        };
      }
    },

    displayResults(data) {
      // predefined list of social media channels
      const socialMediaChannels = ['facebook', 'instagram', 'twitter', 'tiktok', 'linkedin'];
      // push social media data to socialChannels structure
      this.socialChannels = [];

      socialMediaChannels.forEach(channel => {
        const iconComponent = this.getIconComponent(channel);
        const channelData = data[channel] || null; // Use null if the channel data is missing

        this.socialChannels.push({
          key: channel,
          icon: iconComponent, // icon can still be null if getIconComponent doesn't find it
          text: channelData
        });
      });

      this.loading = false;
      this.stopLoadingMessageRotation();
    },

    async pushWebsiteDataToDB() {
      const website_data = this.sessionstore.website_contents;
      const company_id = this.sessionstore.company_id;
      website_data.forEach(page => page.company_id = company_id);
      const { error } = await supabase
        .from('webpages')
        .upsert(website_data, {
          onConflict: 'url',
        });
      if (error) {
        console.error('Error updating website data:', error.message);
      } else {
        console.log('Website data updated successfully');
      }
    },


    deleteChannel(index) {
      this.socialChannels.splice(index, 1);
    },

    async pushSocialMediaLinksToDB() {
      // push socialChannels to the database
      console.log('Pushing social channels to the database...');
      const socialMediaData = {};
      this.socialChannels.forEach(channel => {
        socialMediaData[channel.key] = channel.text;
      });
      console.log('Social media data:', socialMediaData);
      const { error } = await supabase
        .from('companies')
        .update(socialMediaData)
        .eq('company_id', this.sessionstore.company_id);

      this.sessionstore.setSocial(socialMediaData);

      if (error) {
        console.error('Error updating social media channels:', error.message);
      } else {
        console.log('Social media channels updated successfully');
      }
    },

    addChannel() {
      if (this.newChannelText.trim() === '') return;

      const channelKey = this.selectedChannel ? this.selectedChannel.toLowerCase() : null;
      const allowedChannels = ['facebook', 'instagram', 'twitter', 'tiktok', 'linkedin'];

      // Check if the selected channel is valid and part of allowed channels
      if (!channelKey || !allowedChannels.includes(channelKey)) {
        console.error('Invalid or unsupported social media channel');
        return;
      }

      // Check if the channel already exists in socialChannels
      const existingChannel = this.socialChannels.find(channel => channel.key === channelKey);
      if (existingChannel) {
        // If the channel exists and its text is null, update the text
        if (!existingChannel.text || existingChannel.text === null) {
          existingChannel.text = this.newChannelText;
        } else {
          console.error('This channel already has a value.');
        }
      } else {
        // Add the new channel if it doesn't exist
        const iconComponent = this.getIconComponent(channelKey);
        this.socialChannels.push({
          key: channelKey,
          icon: iconComponent,
          text: this.newChannelText
        });
      }

      this.newChannelText = ''; // Reset the input field
    },

    async scrapeInstagram() {
      console.log('Fetching Instagram data...');
      const path = config.API_BASE_URL + '/scrape-instagram';

      try {
        const { data } = await axios.post(path, {
          instagram_url: this.sessionstore.instagram,
        });

        // Extract the relevant data from the response
        const instagramData = {
          company_id: this.sessionstore.company_id,
          username: data.username,
          followers: data.followers,
          media_count: data.media_count,
          media: JSON.stringify(data.media),  // Convert media array to JSON string
          tagged_media: JSON.stringify(data.tagged_media),  // Convert tagged_media array to JSON string
          last_updated: new Date(),
        };

        // Perform the upsert operation
        await supabase
          .from('instagram_data')
          .upsert([instagramData], {
            onConflict: 'company_id',
            ignoreDuplicates: true
          });

        console.log('Instagram data upserted successfully');
        return instagramData; // Return the data object
      } catch (error) {
        console.error('Error fetching Instagram data:', error);
        throw error; // Re-throw the error to be handled by the caller
      }
    },

    /**
     * Handles the social media confirmation process.
     * 
     * This method is triggered when the user clicks the "Conferma" button.
     * It does the following:
     * 1. Pushes the social media links to the database
     * 2. Calls the /scrape-instagram endpoint to get the Instagram data
     * 3. Updates the sessionstore with the Instagram data
     * 4. Shows the metrics page
     * 
     * @returns {Promise<void>} A promise that resolves when the social media confirmation is complete.
     */

    async handleSocialMediaConfirmation() {
      this.isConfirming = true;
      console.log('Showing metrics...\nCompany:', this.sessionstore.company_name, '\nWebsite:', this.sessionstore.website);
      try {
        await this.pushSocialMediaLinksToDB();
        const instagramData = await this.scrapeInstagram();
        console.log('Done fetching Instagram data for ' + instagramData.username);

        // Only show metrics after all data is ready
        this.showMetricsConstant = true;
        this.sessionstore.instagramDataLoaded = true;
        this.showBox = false;
      } catch (error) {
        console.error('Error during social media confirmation:', error);
      } finally {
        this.isConfirming = false;
      }
    },
    showNavigationMenu() {
      this.ShowNavigationMenu = true
    },
    showFollowingPage() {
      if (this.CurrentIndex === 0) {
        this.ShowStorytellingPage = true;
        this.ShowCommunityPage = false;
        this.ShowExperiencePage = false;
        this.ShowFinalPage = false;
        this.CurrentIndex += 1;
        this.ShowNavigationMenu = false;
        this.showMetricsConstant = false;
      } else if (this.CurrentIndex === 1) {
        this.ShowStorytellingPage = false;
        this.ShowCommunityPage = false;
        this.ShowExperiencePage = true;
        this.ShowFinalPage = false;
        this.CurrentIndex += 1;
        this.ShowNavigationMenu = false;
      } else if (this.CurrentIndex === 2) {
        this.ShowStorytellingPage = false;
        this.ShowCommunityPage = true;
        this.ShowExperiencePage = false;
        this.ShowFinalPage = false;
        this.CurrentIndex += 1;
        this.ShowNavigationMenu = false;
      } else if (this.CurrentIndex === 3) {
        this.ShowStorytellingPage = false;
        this.ShowCommunityPage = false;
        this.ShowExperiencePage = false;
        this.ShowFinalPage = true;
        this.CurrentIndex += 1;
        this.ShowNavigationMenu = false;
      }
    },
    showPreviousPage() {
      if (this.CurrentIndex === 1) {
        this.ShowStorytellingPage = false;
        this.ShowCommunityPage = false;
        this.ShowExperiencePage = false;
        this.ShowFinalPage = false;
        this.CurrentIndex -= 1;
        this.ShowNavigationMenu = false;
      } else if (this.CurrentIndex === 2) {
        this.ShowStorytellingPage = true;
        this.ShowCommunityPage = false;
        this.ShowExperiencePage = false;
        this.ShowFinalPage = false;
        this.CurrentIndex -= 1;
        this.ShowNavigationMenu = false;
      } else if (this.CurrentIndex === 3) {
        this.ShowStorytellingPage = false;
        this.ShowCommunityPage = false;
        this.ShowExperiencePage = true;
        this.ShowFinalPage = false;
        this.CurrentIndex -= 1;
        this.ShowNavigationMenu = false;
      } else if (this.CurrentIndex === 4) {
        this.ShowStorytellingPage = false;
        this.ShowCommunityPage = true;
        this.ShowExperiencePage = false;
        this.ShowFinalPage = false;
        this.CurrentIndex -= 1;
        this.ShowNavigationMenu = false;
      }
    },
    startLoadingMessageRotation() {
      this.loadingIntervalId = setInterval(() => {
        this.currentLoadingMessageIndex =
          (this.currentLoadingMessageIndex + 1) % this.loadingMessages.length;
      }, 4000);
    },
    stopLoadingMessageRotation() {
      if (this.loadingIntervalId) {
        clearInterval(this.loadingIntervalId);
        this.loadingIntervalId = null;
      }
    },
    showErrorToast(message) {
      // Use vue-toastification
      this.toast.error(message, {
        timeout: 5000,
        position: 'top-right',
        closeOnClick: true,
        pauseOnHover: true,
      });
    },
  },
  beforeUnmount() {
    this.stopLoadingMessageRotation();
  },
};

</script>

<style lang="scss" scoped>
.social-media-box {
  background: rgba(255, 255, 255, 0.95);
  padding: 2rem;
  border: 2px solid #2e4eed;
  width: 100%;
  max-width: 700px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.social-media-box h3 {
  color: #1a1a1a;
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
}

.social-media-box p {
  color: #1a1a1a;
  text-align: center;
}

.channel {
  display: flex;
  align-items: left;
  width: 100%;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(46, 78, 237, 0.1);
}

.channel:last-child {
  border-bottom: none;
}

.channel>* {
  margin-right: 10px;
}

.elimina-bottone {
  position: inherit;
  cursor: pointer;
  transition: color 0.2s ease;
}

.elimina-bottone:hover {
  color: #2e4eed;
}

.nome-canale {
  position: relative;
  flex: 1;
  margin-left: 25px;
  color: #1a1a1a;
}

.sub-text {
  margin-top: 1rem;
  color: #1a1a1a;
  text-align: center;
  font-size: 0.9rem;
}

.add-channel {
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
}

.add-channel input {
  flex: 1;
  height: 2.5rem;
  padding: 0 1rem;
  border: 2px solid #2e4eed;
  background: rgba(255, 255, 255, 0.95);
}

.add-channel select {
  height: 2.5rem;
  padding: 0 1rem;
  border: 2px solid #2e4eed;
  background: rgba(255, 255, 255, 0.95);
}

.aggiungi-bottone {
  position: inherit;
  cursor: pointer;
  transition: color 0.2s ease;
}

.aggiungi-bottone:hover {
  color: #2e4eed;
}

.confirm-button {
  margin-top: 1.5rem;
  --button-border-color: #2e4eed;
  --button-text-color: #ffffff;
  --button-background-color: #2e4eed;
  --button-background-hover-color: #1e3ed8;
  --button-border-radius: 0;
  width: 100%;
}

.steps {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 10;
}

.matrix-page {
  width: 100vw;
  height: 100vh;
  margin-top: 50px;
  background: transparent;
}

.loading-container {
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 2rem;
  background-color: #f8f9fa;
  border-style: solid;
  border-width: 2px;
  border-color: #2e4eed;
}

.loading-animation {
  width: 64px;
  height: 64px;
}

.loading-text {
  font-size: 1.2rem;
  color: #333;
  text-align: center;
  margin: 0;
  font-weight: 500;
}
</style>
